/**
 * TP/SL Drag Handler
 * Handles drag events for Take Profit and Stop Loss lines
 */

import type { Position } from "@/solana/programs/types";
import { toast } from "sonner";

/**
 * Validate TP price
 */
export const validateTPPrice = (price: number, position: Position): boolean => {
  const isLong = position.side === "long";
  const entryPrice = position.entryPrice;

  if (price <= 0) {
    return false;
  }

  // For longs, TP must be above entry price
  if (isLong && price <= entryPrice) {
    toast.error("Take profit for long position must be above entry price");
    return false;
  }

  // For shorts, TP must be below entry price
  if (!isLong && price >= entryPrice) {
    toast.error("Take profit for short position must be below entry price");
    return false;
  }

  return true;
};

/**
 * Validate SL price
 */
export const validateSLPrice = (price: number, position: Position): boolean => {
  const isLong = position.side === "long";
  const entryPrice = position.entryPrice;
  const liquidationPrice = position.liquidationPrice;

  if (price <= 0) {
    return false;
  }

  // For longs, SL must be below entry price and above liquidation price
  if (isLong && (price >= entryPrice || price <= liquidationPrice)) {
    toast.error("Stop loss for long position must be below entry and above liquidation");
    return false;
  }

  // For shorts, SL must be above entry price and below liquidation price
  if (!isLong && (price <= entryPrice || price >= liquidationPrice)) {
    toast.error("Stop loss for short position must be above entry and below liquidation");
    return false;
  }

  return true;
};

/**
 * Calculate estimated P&L at a given price
 */
export const calculateEstimatedPnL = (
  targetPrice: number,
  position: Position
): { pnl: number; percent: number } => {
  const isLong = position.side === "long";
  const entryPrice = position.entryPrice;
  const pnl = (targetPrice - entryPrice) * position.size * (isLong ? 1 : -1);
  const percent = ((targetPrice - entryPrice) / entryPrice) * 100 * (isLong ? 1 : -1);

  return { pnl, percent };
};

